import { EINVOICE_STATUS } from '@/utils/einvoice.enum'
import { canBootstrapDraftFromFacet } from '@/core/services/store/modules/einvoice/einvoice.adapter'

const ACTION_LABELS = Object.freeze({
  retry: 'Thử lại',
  'bootstrap-draft': 'Tạo nháp mới',
  validate: 'Kiểm tra dữ liệu',
  preview: 'Xem trước',
  issue: 'Phát hành',
  update: 'Chỉnh sửa',
  delete: 'Xoá nháp',
  cancel: 'Huỷ hoá đơn',
  adjust: 'Điều chỉnh',
  replace: 'Thay thế',
  'query-status': 'Kiểm tra trạng thái FAST',
  'query-tax-status': 'Kiểm tra CQT',
  'download-pdf': 'Tải PDF',
  'resend-email': 'Gửi lại email',
})

export const PRE_ISSUED_STATUSES = Object.freeze([
  EINVOICE_STATUS.DRAFT,
  EINVOICE_STATUS.READY_TO_ISSUE,
  EINVOICE_STATUS.ISSUE_FAILED,
  EINVOICE_STATUS.ISSUING,
])

const POST_ISSUED_STATUSES = Object.freeze([
  EINVOICE_STATUS.ISSUED,
  EINVOICE_STATUS.TAX_PENDING,
  EINVOICE_STATUS.TAX_ACCEPTED,
  EINVOICE_STATUS.TAX_REJECTED,
  EINVOICE_STATUS.CANCELED,
  EINVOICE_STATUS.ADJUSTED,
  EINVOICE_STATUS.REPLACED,
])

const PENDING_GENERATION_LABELS = Object.freeze({
  invoiceNo: 'Số hóa đơn sẽ sinh sau khi phát hành',
  pattern: 'Ký hiệu sẽ sinh sau khi phát hành',
  serial: 'Serial sẽ sinh sau khi phát hành',
  key: 'Key FAST sẽ sinh sau phản hồi provider',
  keySearch: 'KeySearch sẽ sinh sau phản hồi provider',
})

function normalizeAction(action) {
  if (typeof action !== 'string') return ''
  return action.trim().toLowerCase()
}

export function normalizeWorkState(workState) {
  if (typeof workState !== 'string') return null
  const normalized = workState.trim().toLowerCase()
  return normalized || null
}

export function isPreIssuedStatus(status) {
  return PRE_ISSUED_STATUSES.includes(status)
}

function asNonNegativeNumber(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return null
  return parsed
}

function collectPendingGeneration(invoice = {}) {
  const pending = Array.isArray(invoice?.readiness?.pendingGeneration)
    ? invoice.readiness.pendingGeneration.map((item) => String(item || '').trim()).filter(Boolean)
    : []

  Object.entries(PENDING_GENERATION_LABELS).forEach(([field, label]) => {
    if (invoice?.fieldSemantics?.[field] === 'pending_generation') {
      pending.push(label)
    }
  })

  return Array.from(new Set(pending))
}

export function buildReadinessMeta(invoice = {}) {
  const summary = invoice?.readinessSummary || {}
  const readiness = invoice?.readiness || {}
  const blockingErrors = Array.isArray(readiness.blockingErrors) ? readiness.blockingErrors : []
  const warnings = Array.isArray(readiness.warnings)
    ? readiness.warnings.map((item) => item?.message || item).filter(Boolean)
    : []
  const pendingGeneration = collectPendingGeneration(invoice)

  const summaryBlockingCount = asNonNegativeNumber(summary.blockingErrorCount)
  const fallbackBlockingCount = blockingErrors.length
  const blockingErrorCount =
    summaryBlockingCount !== null ? summaryBlockingCount : fallbackBlockingCount

  const summaryWarningCount = asNonNegativeNumber(summary.warningCount)
  const warningCount = summaryWarningCount !== null ? summaryWarningCount : warnings.length

  const hasBlockingErrors = blockingErrorCount > 0
  const hasPendingGeneration = pendingGeneration.length > 0

  const summaryReadyFlag =
    typeof summary.isReady === 'boolean'
      ? summary.isReady
      : typeof readiness.isReady === 'boolean'
        ? readiness.isReady
        : !hasBlockingErrors

  return {
    hasBlockingErrors,
    hasPendingGeneration,
    blockingErrorCount,
    warningCount,
    pendingGeneration,
    warnings,
    isReady: summaryReadyFlag,
    isIssueReady: !hasBlockingErrors,
  }
}

function hasExplicitAllowedActionPolicy(invoice = {}) {
  return (
    Array.isArray(invoice?.actionContext?.allowedActions) || Array.isArray(invoice?.allowedActions)
  )
}

function getFeedbackMessage(invoice = {}) {
  if (Array.isArray(invoice.blockingReasons) && invoice.blockingReasons.length) {
    const matching = invoice.blockingReasons.find((item) => item && item.message)
    if (matching?.message) {
      return matching.message
    }
  }

  if (typeof invoice.feedbackContent === 'string' && invoice.feedbackContent.trim()) {
    return invoice.feedbackContent.trim()
  }

  if (typeof invoice.providerMessage === 'string' && invoice.providerMessage.trim()) {
    return invoice.providerMessage.trim()
  }

  return ''
}

export function getLifecycleAllowedActions(invoice = {}) {
  const allowedActionsSource = Array.isArray(invoice?.actionContext?.allowedActions)
    ? invoice.actionContext.allowedActions
    : Array.isArray(invoice?.allowedActions)
      ? invoice.allowedActions
      : []

  return Array.from(
    new Set(allowedActionsSource.map((item) => normalizeAction(item)).filter(Boolean)),
  )
}

export function resolveLifecycleActionAllowance(invoice = {}, action) {
  const normalizedAction = normalizeAction(action)
  if (!normalizedAction) {
    return null
  }

  if (!hasExplicitAllowedActionPolicy(invoice)) {
    return null
  }

  return getLifecycleAllowedActions(invoice).includes(normalizedAction)
}

export function buildLifecycleUiMeta(invoice = {}) {
  const workState = normalizeWorkState(invoice.workState)
  const allowedActions = getLifecycleAllowedActions(invoice)
  const feedbackMessage = getFeedbackMessage(invoice)

  if (!workState) {
    return {
      known: false,
      workState: null,
      title: '',
      message: '',
      variant: 'secondary',
      icon: 'fas fa-info-circle',
      showAlert: false,
      isLoading: false,
      suggestedActionLabels: [],
      allowedActions,
    }
  }

  const suggestedActionLabels = allowedActions.map((action) => ACTION_LABELS[action] || action)

  switch (workState) {
    case 'pending':
      return {
        known: true,
        workState,
        title: 'Đang xử lý với FAST/CQT',
        message:
          'Bản ghi đang ở trạng thái pending. Tạm khóa toàn bộ thao tác cho đến khi backend thoát trạng thái này.',
        variant: 'info',
        icon: 'fas fa-spinner fa-spin',
        showAlert: true,
        isLoading: true,
        suggestedActionLabels,
        allowedActions,
      }
    case 'failed':
      return {
        known: true,
        workState,
        title: 'Lần xử lý gần nhất đã thất bại',
        message:
          feedbackMessage ||
          'Trạng thái failed chỉ cho phép thử lại hoặc tạo nháp mới. Preview, chỉnh sửa và phát hành tiếp là sai contract.',
        variant: 'danger',
        icon: 'fas fa-exclamation-triangle',
        showAlert: true,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
    case 'cancelled':
      return {
        known: true,
        workState,
        title: 'Hoá đơn đã huỷ',
        message: 'Đây là trạng thái terminal. Bản ghi hiện chỉ còn giá trị tra cứu.',
        variant: 'dark',
        icon: 'fas fa-ban',
        showAlert: true,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
    case 'adjusted':
      return {
        known: true,
        workState,
        title: 'Hoá đơn đã điều chỉnh',
        message:
          'Bản ghi gốc đã được điều chỉnh. Chỉ nên giữ các thao tác tra cứu hoặc tải tài liệu hậu phát hành.',
        variant: 'warning',
        icon: 'fas fa-edit',
        showAlert: true,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
    case 'replaced':
      return {
        known: true,
        workState,
        title: 'Hoá đơn đã thay thế',
        message:
          'Bản ghi gốc đã được thay thế bằng hoá đơn khác. Chỉ nên giữ lane tra cứu và tải tài liệu.',
        variant: 'warning',
        icon: 'fas fa-exchange-alt',
        showAlert: true,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
    case 'none':
      return {
        known: true,
        workState,
        title: 'Chưa có hoá đơn điện tử',
        message: 'Chưa tồn tại HĐĐT cho bill này. Hành động hợp lệ duy nhất là tạo nháp.',
        variant: 'secondary',
        icon: 'fas fa-file-medical',
        showAlert: true,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
    default:
      return {
        known: true,
        workState,
        title: '',
        message: '',
        variant: 'secondary',
        icon: 'fas fa-info-circle',
        showAlert: false,
        isLoading: false,
        suggestedActionLabels,
        allowedActions,
      }
  }
}

function hasExplicitAction(invoice = {}, action) {
  if (!Array.isArray(invoice.allowedActions)) return false

  return invoice.allowedActions
    .map((item) => normalizeAction(item))
    .includes(normalizeAction(action))
}

function hasPreviewArtifact(invoice = {}) {
  return Boolean(invoice?.artifacts?.previewPdf?.route || invoice?.artifacts?.previewPdf?.available)
}

function inferJourneyPhase(invoice = {}) {
  const workState = normalizeWorkState(invoice.workState)

  if (workState === 'none') return 'none'
  if (workState === 'draft') return 'draft'
  if (workState === 'pending') return 'processing'
  if (workState === 'failed') return 'failed'
  if (['issued', 'cancelled', 'adjusted', 'replaced'].includes(workState)) {
    return 'post_issue'
  }

  if (isPreIssuedStatus(invoice.status)) {
    return 'draft'
  }

  if (POST_ISSUED_STATUSES.includes(invoice.status)) {
    return 'post_issue'
  }

  return 'unknown'
}

export function buildInvoiceJourneyUiMeta(invoice = {}) {
  const phase = inferJourneyPhase(invoice)
  const workState = normalizeWorkState(invoice.workState)
  const allowedActions = getLifecycleAllowedActions(invoice)
  const blockingErrorCount = Number(
    invoice?.readinessSummary?.blockingErrorCount ||
      invoice?.readiness?.blockingErrors?.length ||
      0,
  )
  const hasBlockingErrors = blockingErrorCount > 0
  const previewAvailable = hasExplicitAction(invoice, 'preview') || hasPreviewArtifact(invoice)
  const canCreateDraft = canBootstrapDraftFromFacet(invoice)

  if (canCreateDraft) {
    return {
      phase: 'none',
      label: 'Tạo draft GTGT',
      shortLabel: 'Tạo draft',
      description: 'Bill chưa có HĐĐT hoặc chỉ còn đường tạo lại draft.',
      actionKind: 'create-draft',
      canCreateDraft: true,
      canOpenExisting: false,
      canOpenPreview: false,
      hasBlockingErrors,
    }
  }

  if (phase === 'draft') {
    return {
      phase,
      label: 'Mở nháp GTGT',
      shortLabel: 'Mở draft',
      description: hasBlockingErrors
        ? 'Draft đang bị chặn và cần xử lý trước khi phát hành.'
        : 'Draft đã tồn tại và có thể tiếp tục rà soát hoặc phát hành.',
      actionKind: 'open-draft',
      canCreateDraft: false,
      canOpenExisting: true,
      canOpenPreview: previewAvailable,
      hasBlockingErrors,
    }
  }

  if (phase === 'processing') {
    return {
      phase,
      label: 'Theo dõi draft đang xử lý',
      shortLabel: 'Đang xử lý',
      description:
        'Bản ghi đã đi vào lane xử lý FAST/CQT. Mở chi tiết để theo dõi thay vì submit lại.',
      actionKind: 'open-processing',
      canCreateDraft: false,
      canOpenExisting: true,
      canOpenPreview: false,
      hasBlockingErrors,
    }
  }

  if (phase === 'failed') {
    return {
      phase,
      label: 'Mở HĐĐT lỗi',
      shortLabel: 'Mở lỗi',
      description:
        'Lần xử lý gần nhất thất bại. Mở chi tiết để đọc lỗi và chọn đường retry phù hợp.',
      actionKind: 'open-failed',
      canCreateDraft: false,
      canOpenExisting: true,
      canOpenPreview: previewAvailable,
      hasBlockingErrors: true,
    }
  }

  return {
    phase: 'post_issue',
    label: 'Mở HĐĐT đã phát hành',
    shortLabel: 'Mở đã phát hành',
    description: 'HĐĐT đã đi qua pha phát hành hoặc hậu phát hành.',
    actionKind: 'open-issued',
    canCreateDraft: false,
    canOpenExisting: true,
    canOpenPreview: false,
    hasBlockingErrors,
  }
}
